import "./PublicationsPage.css";
import React, { useEffect, useRef, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWrittenContributions } from "../hooks/useWrittenContributions";
import WrittenContributionsForm from "../components/WrittenContributionsForm";
import EditPublicationModalBody from "../components/EditPublicationModalBody";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faPlus } from "@fortawesome/free-solid-svg-icons";
import { callDeleteContribution } from "../api/writtenContribution";
import { ModalContext } from "../components/dashboard/store/ModalContext";
import Hero from "../components/Hero";
import { getContributorDisplayName } from "../utils/contributorDisplayName";
import LoadingSpinner from "../components/LoadingSpinner";

interface Props {
  token?: string;
}

const PublicationsPage: React.FC<Props> = ({ token }) => {
  const { contributions, loading, error, loadContributions } =
    useWrittenContributions(token);
  const navigate = useNavigate();
  const { openModal, closeModal } = useContext(ModalContext);

  const [openEditMenuId, setOpenEditMenuId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{
    [key: string]: boolean;
  }>({});

  const autoCloseTimerRef = useRef<number | null>(null);
  const editMenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    loadContributions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentUserPayload = (): any | null => {
    try {
      const storedToken = token || localStorage.getItem("token");
      if (!storedToken) return null;
      const raw = storedToken.startsWith("Bearer ")
        ? storedToken.split(" ")[1]
        : storedToken;
      const parts = raw.split(".");
      if (parts.length < 2) return null;
      const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const payloadJson = decodeURIComponent(
        atob(payloadBase64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      return JSON.parse(payloadJson);
    } catch {
      return null;
    }
  };

  const isUserAuthorOrSuper = (contributionAuthor: any): boolean => {
    const payload = getCurrentUserPayload();
    if (!payload) return false;

    const currentUserId =
      payload.id ||
      payload._id ||
      payload.sub ||
      payload.userId ||
      (payload.user && (payload.user.id || payload.user._id));
    const contributionAuthorId =
      contributionAuthor?._id || contributionAuthor?.id || contributionAuthor;

    const isSuper =
      payload.isSuperUser ||
      payload.is_superuser ||
      payload.isAdmin ||
      payload.is_admin ||
      payload.role === "superuser" ||
      payload.role === "admin" ||
      (Array.isArray(payload.roles) &&
        (payload.roles.includes("superuser") ||
          payload.roles.includes("admin")));

    if (isSuper) return true;
    if (!currentUserId || !contributionAuthorId) return false;
    return String(currentUserId) === String(contributionAuthorId);
  };

  // Auto-close timer
  const startAutoCloseTimer = () => {
    clearAutoCloseTimer();
    autoCloseTimerRef.current = window.setTimeout(() => {
      setOpenEditMenuId(null);
      autoCloseTimerRef.current = null;
    }, 10000);
  };

  const clearAutoCloseTimer = () => {
    if (autoCloseTimerRef.current) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  };

  // Toggle edit menu
  const handleEllipsisClick = (
    contributionId: string,
    contributionAuthor: any,
  ) => {
    if (!isUserAuthorOrSuper(contributionAuthor)) return;

    if (openEditMenuId === contributionId) {
      setOpenEditMenuId(null);
      clearAutoCloseTimer();
    } else {
      setOpenEditMenuId(contributionId);
      startAutoCloseTimer();
    }
  };

  const openEditFieldModal = (
    contribution: { _id: string; title?: string; summary?: string },
    field: "title" | "summary",
  ) => {
    const storedToken = token || localStorage.getItem("token") || "";
    if (!storedToken) {
      showAuthModal();
      return;
    }

    setOpenEditMenuId(null);
    clearAutoCloseTimer();

    const isTitle = field === "title";
    openModal({
      title: isTitle ? "Edit title" : "Edit summary",
      body: (
        <EditPublicationModalBody
          contributionId={contribution._id}
          field={field}
          initialValue={
            isTitle ? contribution.title || "" : contribution.summary || ""
          }
          token={storedToken}
          onSuccess={() => loadContributions({ silent: true })}
          onClose={closeModal}
        />
      ),
    });
  };

  const showAuthModal = () => {
    openModal({
      title: "Authentication Required",
      body: (
        <>
          <p>You need to sign up or log in to add a contribution.</p>
          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <button className="btn" onClick={() => navigate("/login")}>
              Login
            </button>
            <button className="btn " onClick={() => navigate("/signup")}>
              Sign Up
            </button>
          </div>
        </>
      ),
    });
  };

  const showFormModal = () => {
    openModal({
      title: "Add a Post",
      body: (
        <WrittenContributionsForm
          token={token || localStorage.getItem("token") || ""}
          onSuccess={() => loadContributions({ silent: true })}
          onClose={() => {
            closeModal();
          }}
        />
      ),
    });
  };

  const handleDeleteContribution = async (contributionId: string) => {
    const storedToken = token || localStorage.getItem("token") || "";
    if (!storedToken) {
      showAuthModal();
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to permanently delete this contribution?",
      )
    )
      return;

    setActionLoading((s) => ({ ...s, [`delete-${contributionId}`]: true }));
    try {
      await callDeleteContribution(contributionId, storedToken);
      await loadContributions();
      setOpenEditMenuId(null);
      clearAutoCloseTimer();
    } catch (err) {
      console.error("Failed to delete contribution:", err);
    } finally {
      setActionLoading((s) => ({ ...s, [`delete-${contributionId}`]: false }));
    }
  };

  const handleAddClick = () => {
    const storedToken = token || localStorage.getItem("token");

    if (!storedToken) {
      showAuthModal();
      return;
    }

    showFormModal();
  };

  // Outside click detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openEditMenuId) return;
      const ref = editMenuRefs.current[openEditMenuId];
      if (ref && !ref.contains(event.target as Node)) {
        setOpenEditMenuId(null);
        clearAutoCloseTimer();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openEditMenuId]);

  if (loading) {
    return <LoadingSpinner className="loading-spinner-container--fullscreen" />;
  }
  if (error) return <p>Error: {error}</p>;

  return (
    <>
      <Hero page="publications" titleTwo="Publications" />
      <div className="hero-introduction-wrapper publications-extra-space">
        <div className="hero-introduction-container">
          <div className="hero-introduction">
            <p>
              <p>
                Read atricles about Alnarp's Farm, essays, and other
                publications from contributors about farm related topics.
              </p>
            </p>
          </div>
        </div>
      </div>

      <div>
        {contributions.length === 0 && (
          <div className="content-container">
            <div
              className="info-card"
              style={{ maxWidth: "300px", margin: "0 auto" }}
            >
              <p className="center">No contributions yet.</p>
            </div>
          </div>
        )}
      </div>
      {/* Contributions */}
      <div className="content-container publications-container">
        <button
          type="button"
          className="publication-card publication-card--add"
          onClick={handleAddClick}
          aria-label="Add a publication"
        >
          <div className="publication-card--add-cover" aria-hidden="true" />
          <span className="publication-card--add-icon" aria-hidden="true">
            <FontAwesomeIcon icon={faPlus} />
          </span>
          <div
            className="publication-card-text publication-card--add-text"
            aria-hidden="true"
          />
        </button>
        {contributions.map((c: any) => {
          const canEdit = isUserAuthorOrSuper(c.author);
          const isMenuOpen = openEditMenuId === c._id;

          return (
            <div key={c._id} className="publication-card">
              <div
                ref={(el) => {
                  editMenuRefs.current[c._id] = el;
                }}
                className="edit-contributions-container"
                style={{ display: isMenuOpen ? "flex" : "none" }}
              >
                <button
                  className="edit-contribution-btn"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    openEditFieldModal(c, "title");
                  }}
                >
                  Edit title
                </button>

                <button
                  className="edit-contribution-btn"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    openEditFieldModal(c, "summary");
                  }}
                >
                  Edit summary
                </button>

                <button
                  className="edit-contribution-btn"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleDeleteContribution(c._id);
                  }}
                  disabled={!!actionLoading[`delete-${c._id}`]}
                >
                  {actionLoading[`delete-${c._id}`]
                    ? "Deleting..."
                    : "Delete Post"}
                </button>
              </div>

              <Link
                className="publication-card-link"
                to={`/publications/${c.slug}`}
                onClick={() => {
                  clearAutoCloseTimer();
                  setOpenEditMenuId(null);
                }}
              >
                <div
                  className="card-cover-img"
                  style={{
                    backgroundImage: `url(${c?.publicationCover || ""})`,
                  }}
                ></div>

                <div className="publication-card-text">
                  <h4 className="header-ellipsis">{c.title}</h4>
                  <div className="author-and-ellipsi">
                    <p className="capitalized">
                      {getContributorDisplayName(c.author, c.authorDisplayName)}
                    </p>
                    {canEdit && (
                      <button
                        type="button"
                        aria-label="Open edit menu"
                        className="ellipsi-button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          ev.preventDefault();
                          handleEllipsisClick(c._id, c.author);
                        }}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          );
        })}{" "}
      </div>
    </>
  );
};

export default PublicationsPage;
